import type { LinkProps } from "@tanstack/react-router";
import type { PropsWithChildren, ReactNode } from "react";

import IconButton from "./Button/IconButton";
import { HStack, Spacer, VStack } from "./Layout";
import { HeaderTabs } from "./Navigation/HeaderTabs";
import { Text } from "./Typography/Text";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";

type Props = {
  icon: ReactNode;
  title: string;
  tabs: PropsWithChildren<LinkProps>[];
  help?: {
    title: string;
    description: string;
  };
};

/** リスト系画面のヘッダー */
export default function ListPageHeader({
  icon,
  title,
  tabs: options,
  help,
}: Props) {
  return (
    <VStack className="bg-neutral-fill px-12 border-b border-neutral-muted-border shadow-md">
      <HeaderContent icon={icon} title={title} help={help} />
      <Spacer variant="xxs" />
      <HeaderTabs tabs={options} />
    </VStack>
  );
}

function HeaderContent({
  icon,
  title,
  help,
}: {
  icon: ReactNode;
  title: string;
  help?: { title: string; description: string };
}) {
  return (
    <HStack className="items-center pb-4 pt-7">
      {icon}
      <Spacer size={16} />
      <Text variant="h2">{title}</Text>
      {help && <HelpDialog help={help} />}
    </HStack>
  );
}

function HelpDialog({
  help,
}: { help: { title: string; description: string } }) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <IconButton
          className="ml-auto"
          variant="mutedGhost"
          icon="CircleHelpIcon"
          aria-label="Open help dialog"
        />
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{help.title}</DialogTitle>
        </DialogHeader>
        <DialogDescription className="whitespace-pre-wrap text-neutral-on-fill">
          {help.description}
        </DialogDescription>
      </DialogContent>
    </Dialog>
  );
}
